import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A button that opens a popover, closing on an outside click or Escape.
 *
 * Shared by every tick-box dropdown in the app — `ColumnToggle` and the farm
 * picker — so this behaviour is written once rather than drifting apart
 * between two near-identical popovers.
 */
export function CheckMenu({
  wrapClassName,
  trigger,
  menuClassName,
  menuLabel,
  children,
}: {
  wrapClassName: string;
  trigger: (open: boolean, toggle: () => void) => ReactNode;
  menuClassName: string;
  menuLabel: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={wrapClassName} ref={ref}>
      {trigger(open, () => setOpen((v) => !v))}
      {open && (
        <div className={menuClassName} aria-label={menuLabel}>
          {children}
        </div>
      )}
    </div>
  );
}
